import Alert from '@storeblocks/alert';
import LoadingIndicator from '@storeblocks/loading-indicator';
import React from 'react';

import {
  InsuranceDetails,
  OrganizationPillarTypes,
  useInsuranceOverviewList,
} from 'Api/insurance';
import { InsuranceStatus } from 'Constants/insuranceStatus.constants';
import { isAutomaticallyCancellableVehicleInsurance } from 'Pages/CancelInsurancePage/cancelInsurancePage.helpers';
import { AlertContainer } from 'Pages/CancelInsurancePage/cancelInsurancePage.styled';
import { getAppText } from 'Utils/text';
import { NonRiskInsuranceCancellationForm } from './NonRiskInsuranceCancellationForm';
import { RiskInsuranceCancellationForm } from './RiskInsuranceCancellationForm';
import { VehicleCancellationForm } from './VehicleCancellationForm';

type Props = {
  insuranceDetails: InsuranceDetails;
};

export const AutomaticCancellationForm = ({ insuranceDetails }: Props) => {
  const { data, loading, allFailed, someFailed } = useInsuranceOverviewList();

  if (allFailed || someFailed) {
    return (
      <AlertContainer>
        <Alert
          variant="error"
          title={getAppText('misc.error.fetchInsuranceDetailed.title')}
          description={getAppText(
            'misc.error.fetchInsuranceDetailed.description',
          )}
        />
      </AlertContainer>
    );
  }

  if (loading) {
    return (
      <AlertContainer>
        <LoadingIndicator />
      </AlertContainer>
    );
  }

  if (!data.length) {
    return null;
  }

  const cancellingLastInsurance =
    data.filter(
      (insurance) => insurance.insuranceStatus === InsuranceStatus.ACTIVE,
    ).length === 1;

  const isRiskInsurance =
    insuranceDetails.organizationPillar ===
    OrganizationPillarTypes.RISK_INSURANCE;

  if (isAutomaticallyCancellableVehicleInsurance(insuranceDetails)) {
    return (
      <VehicleCancellationForm
        insuranceDetails={insuranceDetails}
        cancellingLastInsurance={cancellingLastInsurance}
      />
    );
  }

  if (isRiskInsurance) {
    return (
      <RiskInsuranceCancellationForm
        insuranceDetails={insuranceDetails}
        cancellingLastInsurance={cancellingLastInsurance}
      />
    );
  }

  return (
    <NonRiskInsuranceCancellationForm
      insuranceDetails={insuranceDetails}
      cancellingLastInsurance={cancellingLastInsurance}
    />
  );
};
